/**
 * Public job page — `/jobs/:jobId` — GitHub issue #301, "[GEO] JobPosting
 * schema markup on public job pages — free Google for Jobs indexing".
 *
 * Entry point named in the ticket ("Public job posting page — /jobs/:id").
 * No route existed for this at all before this issue (see
 * `docs/flows/03-public-job-application.md`'s "Nothing. This flow has
 * zero implementation on any layer" — that doc predates this issue and
 * covers the *application form* half of that flow, which remains out of
 * scope here per that doc's own review status; this page only covers
 * "view a job + get indexed by Google for Jobs").
 *
 * Data: `usePublicJob` (`@/lib/api/apiClient`) ->
 * `GET /api/public/jobs/:jobId` (`apps/workers/src/api/publicJobs.ts`,
 * no auth required — this route is reachable anonymously, matching
 * Google's crawler and an anonymous candidate).
 *
 * JobPosting JSON-LD: built via `@herohire/types`'s
 * `buildJobPostingJsonLd` (shared with the backend's archived/`null`
 * check and, per #301's scope note, reusable unchanged by the Phase 5 SSG
 * rebuild, #282) and rendered into `<head>` via `react-helmet-async`
 * (already used for `SeoHead.tsx`'s per-route SEO tags).
 *
 * Archived jobs (GH issue #246 "archived-jobs class", `status ===
 * 'CLOSED'`): the backend 410s and returns no job body at all, so no
 * JobPosting markup can ever be built for one. This is a client-rendered
 * SPA route (not server-rendered), so the *page's own* HTTP response
 * status is always 200 (the SPA shell) regardless of which job is
 * requested — same limitation as every other dynamic route in this app
 * (`/admin/jobs/:jobId`, etc.). The ticket's AC explicitly allows for
 * this ("returns HTTP 410 (**or removes the JobPosting JSON-LD
 * entirely**)"): this page satisfies the second half by rendering a
 * "no longer available" state with zero `<script type="application/
 * ld+json">` in the document for an archived job. TODO(#282): the Phase
 * 5 SSG/SSR rebuild should make the *page response itself* 410 for
 * archived jobs, not just omit the markup.
 *
 * No timeline/offer-guarantee claims (same rule as GH issue #300 — see
 * `@herohire/types`'s `TIMELINE_CLAIM_PHRASES` doc-comment): every string
 * literal this component renders itself (headings, labels, empty states)
 * is deliberately free of any claim about how fast a candidate will hear
 * back or receive an offer — see `PublicJobPage.test.tsx`'s
 * `[qa:4]`-tagged assertions.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { buildJobPostingJsonLd, type JobPostingSourceData } from '@herohire/types';

import MarketingLayout from '@/components/marketing/MarketingLayout';
import { usePublicJob, reportApiError } from '@/lib/api/apiClient';
import { ApiError } from '@/lib/api/fetchClient';

export default function PublicJobPage(): JSX.Element {
  const { jobId } = useParams<{ jobId: string }>();
  const { data: job, isLoading, isError, error } = usePublicJob(jobId);

  const isArchived = error instanceof ApiError && error.status === 410;
  const isNotFound = error instanceof ApiError && error.status === 404;
  const isUnexpectedError = isError && !isArchived && !isNotFound;

  useEffect(() => {
    if (isUnexpectedError) {
      reportApiError(error, 'Failed to load this job posting');
    }
  }, [isUnexpectedError, error]);

  if (isLoading) {
    return (
      <MarketingLayout>
        <div
          className="container mx-auto max-w-3xl px-4 py-16 text-center"
          data-testid="public-job-loading"
        >
          <p className="text-muted-foreground">Loading job details…</p>
        </div>
      </MarketingLayout>
    );
  }

  // Archived (410) — no JobPosting markup, ever. See module doc-comment.
  if (isArchived) {
    return (
      <MarketingLayout>
        <Helmet>
          <title>Job posting removed | Hero Hire AI</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div
          className="container mx-auto max-w-3xl px-4 py-16 text-center"
          data-testid="public-job-archived"
        >
          <h1 className="text-2xl font-bold">This job posting is no longer available</h1>
          <p className="mt-4 text-muted-foreground">
            The hiring company has closed this role. Check the company's careers page for other open
            positions.
          </p>
        </div>
      </MarketingLayout>
    );
  }

  if (isNotFound || isUnexpectedError || !job) {
    return (
      <MarketingLayout>
        <Helmet>
          <title>Job not found | Hero Hire AI</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div
          className="container mx-auto max-w-3xl px-4 py-16 text-center"
          data-testid="public-job-not-found"
        >
          <h1 className="text-2xl font-bold">Job not found</h1>
          <p className="mt-4 text-muted-foreground">
            This job posting doesn't exist, or isn't published yet.
          </p>
        </div>
      </MarketingLayout>
    );
  }

  const jobPostingSource: JobPostingSourceData = {
    id: job.id,
    title: job.title,
    description: job.description,
    descriptionIsHtml: false,
    datePosted: job.datePosted,
    validThrough: job.validThrough,
    isArchived: false,
    location: job.location,
    hiringOrganizationName: job.hiringOrganizationName,
    jobPageUrl: job.jobPageUrl,
  };
  const jsonLd = buildJobPostingJsonLd(jobPostingSource);

  return (
    <MarketingLayout>
      <Helmet>
        <title>{`${job.title} at ${job.hiringOrganizationName} | Hero Hire AI`}</title>
        <meta
          name="description"
          content={`${job.title} at ${job.hiringOrganizationName}${
            job.location ? ` — ${job.location}` : ''
          }`}
        />
        {jsonLd ? <script type="application/ld+json">{JSON.stringify(jsonLd)}</script> : null}
      </Helmet>

      <article className="container mx-auto max-w-3xl px-4 py-16" data-testid="public-job-page">
        <p className="text-sm font-semibold text-muted-foreground" data-testid="public-job-company">
          {job.hiringOrganizationName}
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight" data-testid="public-job-title">
          {job.title}
        </h1>
        <div className="mt-3 flex flex-wrap gap-3 text-sm text-muted-foreground">
          <span data-testid="public-job-location">
            {job.isRemote ? 'Remote' : job.location || 'Location not specified'}
          </span>
          <span aria-hidden="true">·</span>
          <span data-testid="public-job-employment-type">Full-time</span>
        </div>

        <div
          className="mt-8 whitespace-pre-line text-base leading-relaxed"
          data-testid="public-job-description"
        >
          {job.description}
        </div>

        <p className="mt-10 text-xs text-muted-foreground" data-testid="public-job-apply-note">
          To apply, contact {job.hiringOrganizationName} directly, referencing this posting.
        </p>
      </article>
    </MarketingLayout>
  );
}
